import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowRight, Quote, Menu, X,
  Compass, ScaleIcon, Gauge, TrendingUp, Shield, Heart as HeartIcon, Infinity as InfinityIcon,
  GraduationCap, Briefcase, Rocket, Users,
  Instagram, Youtube, Mail,
} from 'lucide-react';

import LanguageSwitcher from '../components/LanguageSwitcher';
import HeroVisualization from '../components/landing/HeroVisualization';
import WellnessWheelInteractive from '../components/landing/WellnessWheelInteractive';
import { ChampionPreviewBadge } from '../components/WellnessChampion';
import { useTranslation } from '../i18n/LanguageContext';

/* Refined Clinity design system tokens — Apple/Whoop premium */
const NAVY = '#0F172A';
const NAVY_2 = '#1E293B';
const SLATE = '#475569';
const SLATE_2 = '#64748B';
const SLATE_3 = '#94A3B8';
const BORDER = '#E2E8F0';
const SOFT_BG = '#F8FAFC';
const INDIGO = '#4F46E5';
const INDIGO_2 = '#6366F1';

const FOUNDER_PHOTO = 'https://customer-assets.emergentagent.com/job_life-score-hub-5/artifacts/g1y54qbw_WhatsApp%20Image%202026-06-05%20at%209.59.26%20AM.jpeg';

/* Clinity gradient shared by all premium accent headings */
const GRADIENT_STYLE = {
  background: 'linear-gradient(135deg, #4F46E5 0%, #6366F1 35%, #8B5CF6 70%, #A855F7 100%)',
  WebkitBackgroundClip: 'text',
  backgroundClip: 'text',
  color: 'transparent',
  WebkitTextFillColor: 'transparent',
  paddingBottom: '0.06em',
};

const Reveal = ({ children, delay = 0, className = '', y = 32 }) => (
  <motion.div
    initial={{ opacity: 0, y }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: '-80px' }}
    transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1], delay }}
    className={className}
  >
    {children}
  </motion.div>
);

const PrimaryCTA = ({ onClick, children, testid, size = 'lg' }) => {
  const sizes = {
    lg: 'px-9 py-[18px] text-base md:text-[17px]',
    md: 'px-7 py-[14px] text-[15px]',
    sm: 'px-5 py-2.5 text-sm',
  };
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`group inline-flex items-center justify-center gap-2.5 rounded-full font-semibold text-white transition-all duration-300 ease-out hover:-translate-y-0.5 active:translate-y-0 ${sizes[size]}`}
      style={{
        background: NAVY,
        boxShadow: '0 10px 30px -8px rgba(15,23,42,0.45), 0 2px 6px -2px rgba(15,23,42,0.18)',
        letterSpacing: '-0.005em',
      }}
      onMouseEnter={(e) => (e.currentTarget.style.background = NAVY_2)}
      onMouseLeave={(e) => (e.currentTarget.style.background = NAVY)}
    >
      {children}
      <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
    </button>
  );
};

const Eyebrow = ({ children, accent = false }) => (
  <div
    className="text-[11px] sm:text-xs font-bold uppercase"
    style={{ color: accent ? INDIGO : SLATE_3, letterSpacing: '0.25em', fontFamily: 'Inter, sans-serif' }}
  >
    {children}
  </div>
);

/* Heading with optional last-word gradient accent for premium hierarchy */
const H2 = ({ text, gradientWord, testid, className = '' }) => {
  if (gradientWord && text.includes(gradientWord)) {
    const [before, after] = text.split(gradientWord);
    return (
      <h2
        className={`text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] ${className}`}
        style={{ fontFamily: 'Outfit, sans-serif', color: NAVY, letterSpacing: '-0.025em' }}
        data-testid={testid}
      >
        {before}
        <span style={GRADIENT_STYLE}>{gradientWord}</span>
        {after}
      </h2>
    );
  }
  return (
    <h2
      className={`text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] ${className}`}
      style={{ fontFamily: 'Outfit, sans-serif', color: NAVY, letterSpacing: '-0.025em' }}
      data-testid={testid}
    >
      {text}
    </h2>
  );
};

/* Highlights given keywords in Clinity indigo, semibold. */
const HighlightedText = ({ text, keywords = [] }) => {
  if (!text) return null;
  const escaped = keywords
    .filter(Boolean)
    .map((k) => k.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  if (escaped.length === 0) return <>{text}</>;
  const re = new RegExp(`(${escaped.join('|')})`, 'gi');
  const parts = text.split(re);
  return (
    <>
      {parts.map((part, i) =>
        escaped.some((k) => k.toLowerCase() === part.toLowerCase()) ? (
          <span key={i} style={{ color: INDIGO, fontWeight: 600 }}>{part}</span>
        ) : (
          <span key={i}>{part}</span>
        )
      )}
    </>
  );
};

export default function Landing() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const goAssessment = () => navigate('/assessment');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const closeMobileMenu = () => setMobileMenuOpen(false);

  return (
    <div
      className="min-h-screen bg-white"
      data-testid="landing-page"
      style={{ fontFamily: 'Inter, sans-serif', color: NAVY }}
    >
      {/* ============== NAV ============== */}
      <nav
        className="sticky top-0 z-50 backdrop-blur-xl"
        style={{ backgroundColor: 'rgba(255,255,255,0.82)', borderBottom: `1px solid ${BORDER}` }}
        data-testid="landing-nav"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
          <a
            href="/"
            className="text-lg font-bold tracking-tight no-underline"
            style={{ fontFamily: 'Outfit, sans-serif', color: NAVY, letterSpacing: '-0.02em' }}
          >
            Clinity
          </a>
          <div className="flex items-center gap-3 md:gap-4">
            {/* Desktop nav */}
            <div className="hidden md:flex items-center gap-1 text-sm">
              <a href="/improve" data-testid="nav-improve-score" className="px-3 py-2 rounded-full font-semibold no-underline text-slate-700 hover:text-indigo-600 transition-colors">
                Improve Your Score
              </a>
              <a href="/collaborate" data-testid="nav-collaborate" className="px-3 py-2 rounded-full font-semibold no-underline text-slate-700 hover:text-indigo-600 transition-colors">
                Collaborate
              </a>
            </div>
            {/* CTA — always visible (phone + desktop) */}
            <PrimaryCTA onClick={goAssessment} testid="nav-cta-btn" size="sm">
              {t('landing.nav.cta')}
            </PrimaryCTA>
            {/* Mobile hamburger */}
            <button
              onClick={() => setMobileMenuOpen((v) => !v)}
              className="md:hidden inline-flex items-center justify-center w-10 h-10 rounded-full transition-all hover:bg-slate-100"
              aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
              data-testid="nav-mobile-toggle"
              style={{ border: `1px solid ${BORDER}` }}
            >
              {mobileMenuOpen ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
            </button>
          </div>
        </div>

        {/* Mobile menu sheet */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t bg-white" style={{ borderColor: BORDER }} data-testid="nav-mobile-menu">
            <div className="max-w-7xl mx-auto px-6 py-3 flex flex-col">
              <a
                href="/improve"
                onClick={closeMobileMenu}
                data-testid="nav-mobile-improve-score"
                className="px-3 py-3 rounded-xl font-semibold no-underline text-slate-700 hover:bg-slate-50 hover:text-indigo-600 transition-colors"
              >
                Improve Your Score
              </a>
              <a
                href="/collaborate"
                onClick={closeMobileMenu}
                data-testid="nav-mobile-collaborate"
                className="px-3 py-3 rounded-xl font-semibold no-underline text-slate-700 hover:bg-slate-50 hover:text-indigo-600 transition-colors"
              >
                Collaborate
              </a>
            </div>
          </div>
        )}
      </nav>

      {/* ============== 1. HERO ============== */}
      <section className="relative pt-16 md:pt-20 pb-8 md:pb-10 overflow-hidden" data-testid="hero-section">
        <div
          className="absolute top-16 left-1/2 -translate-x-1/2 w-[900px] h-[450px] rounded-full blur-3xl opacity-40 pointer-events-none"
          style={{ background: 'radial-gradient(rgba(99,102,241,0.18) 0%, transparent 70%)' }}
          aria-hidden="true"
        />

        <div className="relative max-w-4xl mx-auto px-6 md:px-12 text-center">
          {/* Trust line */}
          <Reveal>
            <Eyebrow data-testid="hero-trust-line">{t('landing.hero.trust')}</Eyebrow>
          </Reveal>

          {/* Headline — line 1 in Clinity gradient (visible success), line 2 in deep navy (hidden struggle) */}
          <Reveal delay={0.08}>
            <h1
              className="mt-6 font-bold leading-[1.02]"
              style={{ fontFamily: 'Outfit, sans-serif' }}
              data-testid="hero-headline"
            >
              <span
                className="block text-[42px] sm:text-[62px] lg:text-[78px]"
                style={{ ...GRADIENT_STYLE, letterSpacing: '-0.04em' }}
                data-testid="hero-headline-line1"
              >
                {t('landing.hero.h1.line1')}
              </span>
              <span
                className="block text-[38px] sm:text-[56px] lg:text-[72px]"
                style={{ color: NAVY, letterSpacing: '-0.04em' }}
                data-testid="hero-headline-line2"
              >
                {t('landing.hero.h1.line2')}
              </span>
            </h1>
          </Reveal>

          {/* Animation — already plays the "Life Works Better When Everything Works Together" caption on its final scenario */}
          <Reveal delay={0.18}>
            <div className="mt-10 md:mt-12">
              <HeroVisualization />
            </div>
          </Reveal>

          {/* CTA — sits directly under the animation's synthesis caption for strongest conversion */}
          <Reveal delay={0.28}>
            <div className="mt-6 md:mt-8">
              <PrimaryCTA onClick={goAssessment} testid="hero-primary-cta">
                {t('landing.hero.primary')}
              </PrimaryCTA>
            </div>
          </Reveal>

          {/* Supporting copy — Gradient curiosity hook + small subtle value-prop */}
          <Reveal delay={0.36}>
            <div className="mt-10 max-w-5xl mx-auto">
              <p
                className="text-[22px] sm:text-[26px] md:text-[30px] lg:text-[32px] font-bold leading-[1.15] lg:whitespace-nowrap"
                style={{ ...GRADIENT_STYLE, fontFamily: 'Outfit, sans-serif', letterSpacing: '-0.022em' }}
                data-testid="hero-subheadline"
              >
                {t('landing.hero.sub')}
              </p>
              <p
                className="mt-5 text-[14px] sm:text-[15px] md:text-[16px] font-medium leading-[1.6] max-w-xl mx-auto"
                style={{ color: SLATE_2, letterSpacing: '-0.005em' }}
              >
                {t('landing.hero.supporting')}
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ============== 2. WELLNESS WHEEL — "What Makes Up Your Wellness Score" ============== */}
      <section
        id="wheel-section"
        className="relative pt-12 md:pt-16 pb-8 md:pb-10 scroll-mt-16"
        style={{ background: `linear-gradient(180deg, #FFFFFF 0%, ${SOFT_BG} 50%, ${SOFT_BG} 100%)` }}
        data-testid="wheel-section"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <div className="text-center max-w-2xl mx-auto mb-12">
              <Eyebrow accent>{t('landing.wheel.eyebrow')}</Eyebrow>
              <div className="mt-4">
                <H2 text={t('landing.wheel.h2')} gradientWord="Wellness Score" testid="wheel-headline" />
              </div>
              <p className="mt-6 text-lg md:text-xl leading-[1.65]" style={{ color: SLATE_2 }}>
                {t('landing.wheel.sub')}
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="max-w-[520px] mx-auto">
              <WellnessWheelInteractive />
            </div>
          </Reveal>
        </div>
      </section>

      {/* ============== 3. BUILT FOR ============== */}
      <section
        className="relative pt-10 md:pt-14 pb-24 md:pb-32"
        style={{ background: `linear-gradient(180deg, ${SOFT_BG} 0%, #FFFFFF 100%)` }}
        data-testid="built-for-section"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <div className="text-center max-w-2xl mx-auto mb-16">
              <Eyebrow accent>Who it&apos;s for</Eyebrow>
              <div className="mt-4">
                <H2 text={t('landing.builtfor.h2')} gradientWord="how you actually live" testid="built-for-headline" />
              </div>
            </div>
          </Reveal>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { key: 'students',      icon: GraduationCap },
              { key: 'professionals', icon: Briefcase },
              { key: 'entrepreneurs', icon: Rocket },
              { key: 'anyone',        icon: Users },
            ].map((c, i) => (
              <Reveal key={c.key} delay={i * 0.08}>
                <div
                  className="relative bg-white rounded-3xl p-8 transition-all duration-300 hover:-translate-y-1 overflow-hidden"
                  style={{ border: `1px solid ${BORDER}`, boxShadow: '0 1px 3px rgba(15,23,42,0.04)' }}
                  onMouseEnter={(e) => (e.currentTarget.style.boxShadow = '0 20px 40px -16px rgba(15,23,42,0.12)')}
                  onMouseLeave={(e) => (e.currentTarget.style.boxShadow = '0 1px 3px rgba(15,23,42,0.04)')}
                  data-testid={`built-for-${c.key}`}
                >
                  <div
                    className="absolute -top-10 -right-10 w-32 h-32 rounded-full blur-2xl opacity-20 pointer-events-none"
                    style={{ background: `linear-gradient(135deg, ${INDIGO_2}, #A855F7)` }}
                  />
                  <div
                    className="relative w-12 h-12 rounded-2xl flex items-center justify-center text-white mb-5"
                    style={{
                      background: `linear-gradient(135deg, ${INDIGO} 0%, ${INDIGO_2} 100%)`,
                      boxShadow: '0 8px 20px -6px rgba(79,70,229,0.4)',
                    }}
                  >
                    <c.icon className="w-[22px] h-[22px]" />
                  </div>
                  <div className="text-[17px] font-bold mb-2" style={{ fontFamily: 'Outfit, sans-serif', color: NAVY, letterSpacing: '-0.01em' }}>
                    {t(`landing.builtfor.${c.key}.title`)}
                  </div>
                  <div className="text-sm leading-[1.6]" style={{ color: SLATE_2 }}>
                    {t(`landing.builtfor.${c.key}.body`)}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ============== 5. WHAT YOU UNLOCK ============== */}
      <section
        className="relative py-24 md:py-32"
        style={{ background: `linear-gradient(180deg, ${SOFT_BG} 0%, #FFFFFF 100%)` }}
        data-testid="unlock-section"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <div className="text-center max-w-2xl mx-auto mb-16">
              <Eyebrow accent>{t('landing.unlock.eyebrow')}</Eyebrow>
              <div className="mt-4">
                <H2 text={t('landing.unlock.h2')} gradientWord="Unlock" />
              </div>
              <p className="mt-6 text-lg md:text-xl leading-[1.65]" style={{ color: SLATE_2 }}>
                {t('landing.unlock.sub')}
              </p>
            </div>
          </Reveal>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-7 gap-4">
            {[
              { key: 'clarity',     icon: Compass },
              { key: 'balance',     icon: ScaleIcon },
              { key: 'performance', icon: Gauge },
              { key: 'growth',      icon: TrendingUp },
              { key: 'resilience',  icon: Shield },
              { key: 'fulfillment', icon: HeartIcon },
              { key: 'longevity',   icon: InfinityIcon },
            ].map((c, i) => (
              <Reveal key={c.key} delay={i * 0.06}>
                <div
                  className="bg-white rounded-2xl p-6 text-center transition-all duration-300 hover:-translate-y-1"
                  style={{ border: `1px solid ${BORDER}`, boxShadow: '0 1px 3px rgba(15,23,42,0.04)' }}
                  onMouseEnter={(e) => (e.currentTarget.style.boxShadow = '0 16px 32px -14px rgba(15,23,42,0.12)')}
                  onMouseLeave={(e) => (e.currentTarget.style.boxShadow = '0 1px 3px rgba(15,23,42,0.04)')}
                  data-testid={`unlock-card-${c.key}`}
                >
                  <div className="w-11 h-11 rounded-xl mx-auto mb-3.5 flex items-center justify-center" style={{ background: '#EEF2FF' }}>
                    <c.icon className="w-5 h-5" style={{ color: INDIGO }} />
                  </div>
                  <div className="text-[13px] font-bold" style={{ fontFamily: 'Outfit, sans-serif', color: NAVY }}>
                    {t(`landing.unlock.${c.key}`)}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ============== 6. WELLNESS CHAMPION PROMO ============== */}
      <section
        className="relative py-24 md:py-32 overflow-hidden"
        style={{ background: 'radial-gradient(circle at 80% 0%, #1E1B4B 0%, #0F0F23 60%, #050514 100%)' }}
        data-testid="champion-promo-section"
      >
        <div
          aria-hidden="true"
          className="absolute -top-20 right-0 w-[600px] h-[400px] rounded-full blur-3xl opacity-50 pointer-events-none"
          style={{ background: 'radial-gradient(rgba(168,85,247,0.5) 0%, transparent 70%)' }}
        />
        <div
          aria-hidden="true"
          className="absolute -bottom-20 left-0 w-[500px] h-[400px] rounded-full blur-3xl opacity-30 pointer-events-none"
          style={{ background: 'radial-gradient(rgba(99,102,241,0.5) 0%, transparent 70%)' }}
        />

        <div className="relative max-w-6xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Copy */}
            <Reveal>
              <div>
                <div className="inline-flex items-center gap-2 text-[10px] sm:text-[11px] font-bold uppercase tracking-[0.32em] text-[#C7D2FE] mb-5">
                  <span className="w-1 h-1 rounded-full bg-[#C7D2FE]" />
                  {t('landing.champion.eyebrow')}
                  <span className="w-1 h-1 rounded-full bg-[#C7D2FE]" />
                </div>
                <h2
                  className="text-[32px] sm:text-[44px] lg:text-[56px] font-bold leading-[1.05] text-white"
                  style={{ fontFamily: 'Outfit, sans-serif', letterSpacing: '-0.032em' }}
                  data-testid="champion-promo-headline"
                >
                  {t('landing.champion.h2')}
                </h2>
                <p className="mt-7 text-base sm:text-[17px] leading-[1.7] text-[#CBD5E1] max-w-xl">
                  {t('landing.champion.body')}
                </p>
                <div className="mt-9">
                  <button
                    onClick={goAssessment}
                    data-testid="champion-promo-cta"
                    className="group inline-flex items-center gap-3 px-8 py-[14px] rounded-full font-semibold text-[15px] text-white transition-all duration-300 hover:-translate-y-0.5"
                    style={{
                      background: 'linear-gradient(135deg, #6366F1 0%, #8B5CF6 50%, #A855F7 100%)',
                      boxShadow: '0 20px 50px -15px rgba(168,85,247,0.55), inset 0 1px 0 rgba(255,255,255,0.18)',
                      letterSpacing: '-0.005em',
                    }}
                  >
                    {t('landing.champion.cta')}
                    <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
                  </button>
                </div>
              </div>
            </Reveal>

            {/* Preview badge */}
            <Reveal delay={0.12}>
              <div className="flex justify-center lg:justify-end">
                <ChampionPreviewBadge score={82} />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ============== 5. FOUNDER STORY ============== */}
      <section
        className="relative py-24 md:py-32"
        style={{ background: `linear-gradient(180deg, #FFFFFF 0%, ${SOFT_BG} 100%)`, borderTop: '1px solid #F1F5F9' }}
        data-testid="founder-section"
      >
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Photo */}
            <Reveal>
              <div className="flex justify-center lg:justify-start">
                <div className="relative">
                  <div
                    className="absolute inset-0 rounded-[2rem] blur-2xl opacity-50 pointer-events-none"
                    style={{ background: 'radial-gradient(circle, rgba(99,102,241,0.25) 0%, transparent 70%)' }}
                  />
                  <div
                    className="relative w-72 h-72 sm:w-80 sm:h-80 lg:w-[380px] lg:h-[440px] rounded-[2rem] overflow-hidden"
                    style={{
                      background: 'linear-gradient(135deg, #EEF2FF 0%, #E0E7FF 100%)',
                      border: `1px solid #E0E7FF`,
                      boxShadow: '0 24px 60px -20px rgba(79,70,229,0.25)',
                    }}
                  >
                    <img
                      src={FOUNDER_PHOTO}
                      alt={`${t('landing.founder.name')} — ${t('landing.founder.role')}`}
                      loading="lazy"
                      className="w-full h-full object-cover"
                      data-testid="founder-portrait"
                    />
                    <div
                      className="absolute inset-x-0 bottom-0 h-32 pointer-events-none"
                      style={{ background: 'linear-gradient(to top, rgba(15,23,42,0.45) 0%, transparent 100%)' }}
                    />
                  </div>
                </div>
              </div>
            </Reveal>

            {/* Story */}
            <Reveal delay={0.1}>
              <div>
                <Eyebrow accent>{t('landing.founder.eyebrow')}</Eyebrow>
                <div className="mt-4">
                  <h2
                    className="text-[32px] sm:text-[40px] lg:text-[48px] font-bold leading-tight"
                    style={{ fontFamily: 'Outfit, sans-serif', color: NAVY, letterSpacing: '-0.028em' }}
                    data-testid="founder-headline"
                  >
                    {(() => {
                      const text = t('landing.founder.h2');
                      if (text.includes('Clinity')) {
                        const [b, a] = text.split('Clinity');
                        return (<>{b}<span style={GRADIENT_STYLE}>Clinity</span>{a}</>);
                      }
                      return text;
                    })()}
                  </h2>
                </div>
                <div className="mt-8 space-y-5 leading-[1.7]" style={{ color: SLATE }}>
                  <p className="flex gap-3 text-[15px] md:text-base">
                    <Quote className="w-5 h-5 shrink-0 mt-1" style={{ color: INDIGO }} />
                    <span>{t('landing.founder.body.p1')}</span>
                  </p>
                  <p className="text-[15px] md:text-base">{t('landing.founder.body.p2')}</p>
                  <p className="text-[15px] md:text-base">
                    {(() => {
                      const text = t('landing.founder.body.p3');
                      if (text.includes('Clinity')) {
                        const [b, a] = text.split('Clinity');
                        return (<>{b}<span className="font-semibold" style={{ color: NAVY }}>Clinity</span>{a}</>);
                      }
                      return text;
                    })()}
                  </p>
                </div>
                <div className="mt-10 pt-6" style={{ borderTop: `1px solid ${BORDER}` }}>
                  <p
                    className="text-base sm:text-[17px] font-semibold"
                    data-testid="founder-name"
                    style={{ color: NAVY, fontFamily: 'Outfit, sans-serif' }}
                  >
                    {t('landing.founder.name')}
                  </p>
                  <p
                    className="text-sm font-semibold tracking-wider mt-1"
                    data-testid="founder-degree"
                    style={{ color: INDIGO, letterSpacing: '0.08em' }}
                  >
                    {t('landing.founder.degree')}
                  </p>
                  <p className="text-sm mt-1" style={{ color: SLATE_2 }}>
                    {t('landing.founder.role')}
                  </p>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ============== CONNECT WITH US — Instagram + WhatsApp QR cards ============== */}
      <ConnectWithUsSection />

      {/* ============== FINAL CTA ============== */}
      <section
        className="relative py-24 md:py-32 overflow-hidden"
        style={{ background: SOFT_BG }}
        data-testid="final-cta-section"
      >
        <div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[400px] rounded-full blur-3xl opacity-25 pointer-events-none"
          style={{ background: 'radial-gradient(rgba(99,102,241,0.2) 0%, transparent 70%)' }}
          aria-hidden="true"
        />
        <div className="relative max-w-3xl mx-auto px-6 md:px-12 text-center">
          <Reveal>
            <H2 text={t('landing.final.h2')} testid="final-cta-headline" />
            <p className="mt-7 text-lg md:text-xl leading-[1.7] max-w-2xl mx-auto" style={{ color: SLATE_2 }}>
              {t('landing.final.sub')}
            </p>
            <div className="mt-10">
              <PrimaryCTA onClick={goAssessment} testid="final-cta-btn">
                {t('landing.final.cta')}
              </PrimaryCTA>
            </div>
            <p className="mt-5 text-sm" style={{ color: SLATE_3 }}>
              {t('landing.final.subtext')}
            </p>
          </Reveal>
        </div>
      </section>

      {/* ============== LANGUAGE STRIP (above footer — website preference) ============== */}
      <section
        className="py-8"
        style={{ borderTop: `1px solid ${BORDER}`, background: '#FBFBFD' }}
        data-testid="language-strip"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4">
          <span
            className="text-[13px] font-medium"
            style={{ color: SLATE_2, fontFamily: 'Inter, sans-serif' }}
          >
            Language
          </span>
          <div data-testid="footer-language-switcher">
            <LanguageSwitcher />
          </div>
        </div>
      </section>

      {/* ============== FOOTER ============== */}
      <footer className="py-14" data-testid="landing-footer">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <div className="text-base font-bold" style={{ color: NAVY, fontFamily: 'Outfit, sans-serif', letterSpacing: '-0.02em' }}>
              Clinity
            </div>
            <div className="text-xs mt-1.5" style={{ color: SLATE_3 }}>
              © {new Date().getFullYear()} Clinity. Your Prescription for Complete Wellbeing™
            </div>
          </div>
          <div className="flex items-center gap-5" style={{ color: SLATE_2 }}>
            <a href="https://instagram.com/clinity_wellness" target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="transition-colors hover:text-[#0F172A]"><Instagram className="w-5 h-5" /></a>
            <a href="https://youtube.com/@clinity_wellness" target="_blank" rel="noopener noreferrer" aria-label="YouTube" className="transition-colors hover:text-[#0F172A]"><Youtube className="w-5 h-5" /></a>
            <a href="mailto:hello@clinity.in" aria-label="Email" className="transition-colors hover:text-[#0F172A]"><Mail className="w-5 h-5" /></a>
          </div>
        </div>
      </footer>
    </div>
  );
}

/* ============== CONNECT WITH US — Instagram + WhatsApp Channel ============== */
const INSTAGRAM_URL = 'https://www.instagram.com/clinity_?igsh=bWg5cXpzbm5rbzV6&utm_source=qr';
const WHATSAPP_URL  = 'https://whatsapp.com/channel/0029VaybO98L2AU2cTb02r2Y';
// Generate clean, high-contrast QR codes directly from the destination URLs.
// Black-on-white for maximum scanner reliability; no branding on the code itself.
const qrFor = (url) =>
  `https://api.qrserver.com/v1/create-qr-code/?data=${encodeURIComponent(url)}&size=480x480&margin=8&ecc=M&format=svg`;
const INSTAGRAM_QR = qrFor(INSTAGRAM_URL);
const WHATSAPP_QR  = qrFor(WHATSAPP_URL);

/* Inline gradient Instagram glyph — reused as the "logo" on the card */
const InstagramLogo = ({ className = '' }) => (
  <svg
    viewBox="0 0 48 48"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
    aria-hidden="true"
  >
    <defs>
      <linearGradient id="ig-grad-connect" x1="0" y1="48" x2="48" y2="0">
        <stop offset="0%"   stopColor="#F09433" />
        <stop offset="25%"  stopColor="#E6683C" />
        <stop offset="50%"  stopColor="#DC2743" />
        <stop offset="75%"  stopColor="#CC2366" />
        <stop offset="100%" stopColor="#BC1888" />
      </linearGradient>
    </defs>
    <rect x="2" y="2" width="44" height="44" rx="12" fill="url(#ig-grad-connect)" />
    <rect x="10" y="10" width="28" height="28" rx="8" fill="none" stroke="white" strokeWidth="2.6" />
    <circle cx="24" cy="24" r="6.5" fill="none" stroke="white" strokeWidth="2.6" />
    <circle cx="33.5" cy="14.5" r="1.9" fill="white" />
  </svg>
);

/* Official WhatsApp glyph */
const WhatsAppLogo = ({ className = '' }) => (
  <svg
    viewBox="0 0 48 48"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
    aria-hidden="true"
  >
    <defs>
      <linearGradient id="wa-grad-connect" x1="0" y1="0" x2="48" y2="48">
        <stop offset="0%"   stopColor="#25D366" />
        <stop offset="100%" stopColor="#128C7E" />
      </linearGradient>
    </defs>
    <rect x="2" y="2" width="44" height="44" rx="12" fill="url(#wa-grad-connect)" />
    <path
      fill="#fff"
      d="M32.6 15.4a11.4 11.4 0 0 0-19.2 12.4l-1.5 5.4 5.5-1.4a11.4 11.4 0 0 0 15.7-16.4Zm-8 17.4a9.4 9.4 0 0 1-4.8-1.3l-.3-.2-3.2.8.9-3.1-.2-.3a9.4 9.4 0 1 1 7.6 4Zm5.4-7c-.3-.2-1.7-.9-2-1s-.5-.2-.7.1c-.2.3-.8 1-1 1.2-.2.2-.4.2-.7.1-.3-.1-1.3-.5-2.4-1.5a9 9 0 0 1-1.7-2c-.2-.3 0-.5.1-.6l.4-.5c.1-.2.2-.3.3-.5.1-.2 0-.4 0-.5l-1-2.4c-.2-.6-.5-.5-.7-.5h-.6c-.2 0-.5 0-.8.4-.3.3-1 1-1 2.5s1 2.9 1.2 3.1c.1.2 2 3 4.7 4.2l1.6.6c.7.2 1.2.2 1.7.1.5-.1 1.7-.7 1.9-1.3.2-.7.2-1.2.2-1.3l-.4-.3Z"
    />
  </svg>
);

function ConnectCard({ testid, href, LogoComp, qrSrc, label, accentColor, logoLabel }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={testid}
      className="group relative flex flex-col items-center p-8 md:p-10 rounded-[24px] bg-white no-underline overflow-hidden transition-all duration-300 ease-out will-change-transform hover:-translate-y-1.5"
      style={{
        border: '1px solid #E2E8F0',
        boxShadow: '0 2px 6px rgba(15,23,42,0.04)',
      }}
      onMouseEnter={(e) => (e.currentTarget.style.boxShadow = '0 30px 60px -22px rgba(15,23,42,0.18), 0 10px 22px -12px rgba(15,23,42,0.10)')}
      onMouseLeave={(e) => (e.currentTarget.style.boxShadow = '0 2px 6px rgba(15,23,42,0.04)')}
    >
      {/* Subtle brand-color halo — appears on hover */}
      <div
        aria-hidden="true"
        className="absolute -top-16 left-1/2 -translate-x-1/2 w-64 h-64 rounded-full blur-3xl opacity-0 group-hover:opacity-40 transition-opacity duration-500 pointer-events-none"
        style={{ background: `radial-gradient(circle, ${accentColor}55 0%, transparent 70%)` }}
      />

      {/* Logo */}
      <div
        className="relative w-16 h-16 md:w-[72px] md:h-[72px] transition-transform duration-300 group-hover:scale-110"
        aria-label={logoLabel}
      >
        <LogoComp className="w-full h-full" />
      </div>

      {/* QR code — clickable via parent <a> */}
      <div
        className="relative mt-7 md:mt-8 p-3 md:p-4 rounded-[18px] bg-white transition-all duration-300 group-hover:-translate-y-0.5"
        style={{
          border: '1px solid #E2E8F0',
          boxShadow: '0 8px 22px -10px rgba(15,23,42,0.15), 0 2px 4px rgba(15,23,42,0.04)',
        }}
      >
        <img
          src={qrSrc}
          alt={`${logoLabel} QR code`}
          loading="lazy"
          className="block w-[168px] h-[168px] md:w-[192px] md:h-[192px] object-contain"
          data-testid={`${testid}-qr`}
        />
      </div>

      {/* Label */}
      <div
        className="mt-7 md:mt-8 text-[15px] md:text-base font-semibold text-[#0F172A] flex items-center gap-2"
        style={{ fontFamily: 'Outfit, sans-serif', letterSpacing: '-0.01em' }}
      >
        {label}
        <ArrowRight
          className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1"
          style={{ color: accentColor }}
        />
      </div>
    </a>
  );
}

function ConnectWithUsSection() {
  return (
    <section
      className="relative py-20 md:py-28"
      style={{ background: `linear-gradient(180deg, ${SOFT_BG} 0%, #FFFFFF 100%)` }}
      data-testid="connect-with-us-section"
    >
      {/* Soft indigo halo */}
      <div
        aria-hidden="true"
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] h-[420px] rounded-full blur-3xl opacity-25 pointer-events-none"
        style={{ background: 'radial-gradient(rgba(99,102,241,0.18) 0%, transparent 70%)' }}
      />

      <div className="relative max-w-5xl mx-auto px-6 md:px-12">
        <Reveal>
          <div className="text-center max-w-2xl mx-auto mb-12 md:mb-14">
            <Eyebrow accent>Community</Eyebrow>
            <div className="mt-4">
              <H2 text="Connect With Us" testid="connect-heading" />
            </div>
            <p
              className="mt-6 text-lg md:text-xl leading-[1.65]"
              style={{ color: SLATE_2 }}
              data-testid="connect-subheading"
            >
              Stay connected with Clinity for wellness insights, expert guidance,
              updates, and early access to everything we&apos;re building.
            </p>
          </div>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
          <Reveal delay={0.05}>
            <ConnectCard
              testid="connect-instagram-card"
              href={INSTAGRAM_URL}
              LogoComp={InstagramLogo}
              qrSrc={INSTAGRAM_QR}
              label="Follow us on Instagram"
              logoLabel="Instagram"
              accentColor="#DC2743"
            />
          </Reveal>
          <Reveal delay={0.12}>
            <ConnectCard
              testid="connect-whatsapp-card"
              href={WHATSAPP_URL}
              LogoComp={WhatsAppLogo}
              qrSrc={WHATSAPP_QR}
              label="Join our WhatsApp Channel"
              logoLabel="WhatsApp"
              accentColor="#25D366"
            />
          </Reveal>
        </div>
      </div>
    </section>
  );
}
